'use client';

import { ButtonV2 } from '@/components/ui/ButtonV2';
import type { RuntimeToolRunAgUiState } from '@/lib/runtimeTools/toolRuns';
import { getRuntimeToolManifest, type RuntimeToolId } from '@/lib/runtimeTools/manifest';

type Props = {
  state: RuntimeToolRunAgUiState | null;
  error: string | null;
  connectionStatus: string;
  selectedToolLabel?: string | null;
  onOpenArtifact?: (artifactId: string) => void;
};

type RendererProps = {
  state: RuntimeToolRunAgUiState | null;
  error: string | null;
  connectionStatus: string;
  onOpenArtifact?: (artifactId: string) => void;
};

function BaseShell(props: RendererProps & { title: string; body: string; children?: React.ReactNode; tone?: 'default' | 'error' }) {
  const { title, body, children, tone = 'default' } = props;
  return (
    <div className={`mt-4 rounded-2xl border px-4 py-3 ${
      tone === 'error'
        ? 'border-destructive/30 bg-destructive/10'
        : 'border-border/50 bg-background/80'
    }`}>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className={`mt-1 text-sm ${tone === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
        {body}
      </p>
      {children}
    </div>
  );
}

function ArtifactRenderer(props: RendererProps) {
  const { state, error, connectionStatus, onOpenArtifact } = props;
  const title = state?.label || 'Tool run';
  const body = error || state?.summary || 'Tool run started.';
  const failed = state?.status === 'failed' || Boolean(error);
  return (
    <BaseShell title={title} body={body} tone={failed ? 'error' : 'default'}>
      <p className="mt-2 text-xs text-muted-foreground">
        Status: {state?.status || connectionStatus}
      </p>
      {state?.resultPayload?.title ? (
        <p className="mt-3 text-sm font-medium text-foreground">{state.resultPayload.title}</p>
      ) : null}
      {state?.resultPayload?.content ? (
        <div className="mt-3 rounded-xl border border-border/40 bg-background/70 px-3 py-3 text-sm text-muted-foreground">
          <p className="line-clamp-5 whitespace-pre-wrap">{state.resultPayload.content}</p>
        </div>
      ) : null}
      {state?.createdArtifactIds?.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {state.createdArtifactIds.map((artifactId) => (
            <ButtonV2
              key={`${state.requestId}-${artifactId}`}
              variant="secondary"
              size="sm"
              onClick={() => onOpenArtifact?.(artifactId)}
            >
              Open {artifactId}
            </ButtonV2>
          ))}
        </div>
      ) : null}
    </BaseShell>
  );
}

function AnalysisRenderer(props: RendererProps) {
  const { state, error, connectionStatus } = props;
  const title = state?.label || 'Analysis';
  const body = error || state?.summary || 'Analysis running.';
  const failed = state?.status === 'failed' || Boolean(error);
  return (
    <BaseShell title={title} body={body} tone={failed ? 'error' : 'default'}>
      <p className="mt-2 text-xs text-muted-foreground">
        Status: {state?.status || connectionStatus}
      </p>
      {state?.resultPayload?.content ? (
        <div className="mt-3 rounded-xl border border-border/40 bg-background/70 px-3 py-3 text-sm text-muted-foreground">
          <p className="whitespace-pre-wrap">{state.resultPayload.content}</p>
        </div>
      ) : null}
    </BaseShell>
  );
}

function WorkflowRenderer(props: RendererProps) {
  const { state, error, connectionStatus, onOpenArtifact } = props;
  const title = state?.label || 'Workflow';
  const body = error || state?.summary || 'Workflow started.';
  const failed = state?.status === 'failed' || Boolean(error);
  return (
    <BaseShell title={title} body={body} tone={failed ? 'error' : 'default'}>
      <p className="mt-2 text-xs text-muted-foreground">
        Status: {state?.status || connectionStatus}
      </p>
      {state?.createdArtifactIds?.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {state.createdArtifactIds.map((artifactId) => (
            <ButtonV2
              key={`${state.requestId}-${artifactId}`}
              variant="secondary"
              size="sm"
              onClick={() => onOpenArtifact?.(artifactId)}
            >
              Open {artifactId}
            </ButtonV2>
          ))}
        </div>
      ) : null}
    </BaseShell>
  );
}

const RENDERER_REGISTRY: Record<string, (props: RendererProps) => JSX.Element> = {
  'artifact-default': ArtifactRenderer,
  'artifact-review': ArtifactRenderer,
  'analysis-default': AnalysisRenderer,
  'workflow-default': WorkflowRenderer,
};

function resolveRenderer(toolId: RuntimeToolId | null | undefined) {
  if (!toolId) return ArtifactRenderer;
  const manifest = getRuntimeToolManifest(toolId);
  const rendererKey = manifest?.rendererKey || 'artifact-default';
  return RENDERER_REGISTRY[rendererKey] || ArtifactRenderer;
}

export function RuntimeToolResultPanel(props: Props) {
  const { state, error, connectionStatus, selectedToolLabel, onOpenArtifact } = props;
  if (!state && !error) return null;

  const Renderer = resolveRenderer(state?.toolId);
  if (!state && error) {
    return (
      <BaseShell title={selectedToolLabel || 'Tool run'} body={error} tone="error" />
    );
  }

  return (
    <Renderer
      state={state}
      error={error}
      connectionStatus={connectionStatus}
      onOpenArtifact={onOpenArtifact}
    />
  );
}
